import { motion } from 'framer-motion';
import { Star, FileText } from 'lucide-react';
import SectionHead from '@/components/SectionHead';
import { COACHES, CONTACT, ASSETS } from '@/data/content';
import { fadeUp, stagger, viewportOnce } from '@/lib/motion';

export default function Coaches() {
  return (
    <section id="coaches" className="relative py-24 sm:py-32 bg-night overflow-hidden" data-testid="coaches-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(197,160,89,0.07),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead
          chapter="07"
          eyebrow="Coaching Staff"
          title={'INTERNATIONAL\nEXPERIENCE.'}
          description="ASA is led by coaches with national team backgrounds and internationally recognized licenses."
          testId="coaches-heading"
        />

        <motion.div
          variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="grid lg:grid-cols-2 gap-6"
        >
          {COACHES.map((c, i) => (
            <motion.article
              key={c.name} variants={fadeUp} custom={i}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden rounded-sm border border-white/10 bg-night-surface hover:border-brand-gold/50 transition-[border-color,transform] duration-300"
              data-testid={`coach-card-${i}`}
            >
              <div className="relative h-72 sm:h-80 overflow-hidden">
                {c.image ? (
                  <img src={c.image} alt={c.name} style={{ objectPosition: c.imagePos || 'center top' }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                ) : (
                  <div className="w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(197,160,89,0.18),#0a0a0c_70%)] flex items-center justify-center">
                    <div className="relative">
                      <img src={ASSETS.logo} alt="ASA crest" className="w-40 h-40 rounded-full object-cover opacity-90 ring-2 ring-brand-gold/40" />
                      <span className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent animate-gold-sweep pointer-events-none" />
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-night-surface via-transparent to-transparent" />
                <div className="absolute top-4 left-4 bg-brand-red text-white text-[10px] font-bold tracking-[0.3em] uppercase px-3 py-1.5 rounded-sm" data-testid={`coach-role-${i}`}>
                  {c.role}
                </div>
              </div>

              <div className="p-8">
                <div className="flex gap-1 text-brand-gold mb-3" aria-hidden="true">
                  {[0, 1, 2].map(s => <Star key={s} size={14} fill="currentColor" />)}
                </div>
                <h3 className="font-display text-3xl tracking-wide text-white uppercase">{c.name}</h3>
                <p className="mt-2 text-xs sm:text-sm uppercase tracking-[0.18em] text-brand-gold font-semibold leading-relaxed">{c.credentials}</p>
                <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed">{c.bio}</p>
                <a
                  href={c.resumeUrl || `${CONTACT.whatsapp.split('?')[0]}?text=${encodeURIComponent(`Hello ASA, I'd like to view the official resume of ${c.name}.`)}`}
                  target="_blank" rel="noreferrer"
                  data-testid={`coach-resume-cta-${i}`}
                  className="mt-6 inline-flex items-center gap-2 border border-brand-gold/60 text-brand-gold hover:bg-brand-gold hover:text-night font-display tracking-widest text-lg px-6 py-3 rounded-sm transition-colors duration-300"
                >
                  <FileText size={17} /> VIEW OFFICIAL RESUME
                </a>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
